import buttonClose from '@assets/button-close.svg';
import Button from '@components/Button';
import Input from '@components/Input';
import { ModalType } from '@data/types';
import useQuestionnaireStore from '@store/questionnaire';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';

interface Props extends ModalType {}

const ModalUserDetails: React.FC<Props> = ({
  setDisplayModal = () => {},
  displayModal,
}) => {
  const defaultSubmitText = 'Save';
  const [submitText, setSubmitText] = useState<string>(defaultSubmitText);
  const [isFadingIn, setIsFadingIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | boolean>(false);
  const {
    setQuestionIndexStore,
    setPillarIndexStore,
    setFirstName,
    setLastName,
    setEmail,
    setCompany,
    setPrivacyPolicy,
  } = useQuestionnaireStore();

  useEffect(() => {
    if (displayModal) {
      setTimeout(() => setIsFadingIn(true), 50);
    } else {
      setIsFadingIn(false);
    }
  }, [displayModal]);

  return (
    <div
      id="modal"
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
    >
      <div
        className={classNames(
          'bg-white rounded-lg overflow-hidden shadow-lg max-w-innerGrid w-full flex justify-center flex-col transition-opacity duration-200 ease-in',
          {
            'opacity-100': isFadingIn,
            'opacity-0': !isFadingIn,
          }
        )}
      >
        <div className="flex mb-auto">
          <button
            className="ml-auto p-4 pb-0 hover:opacity-50"
            onClick={() => setDisplayModal(!displayModal)}
          >
            <img src={buttonClose} alt="close" />
          </button>
        </div>

        <div className="mb-auto">
          <div className="bg-gray-100 px-8">
            <h2 className="text-1xl font-lightNormal">Before you begin</h2>
          </div>

          <div className="p-8 pt-3">
            <p className="font-base text-sm">
              Enter your details below to proceed.
            </p>

            <form
              className="pt-8 flex flex-wrap -m-4"
              onSubmit={async (event: React.FormEvent<HTMLFormElement>) => {
                event.preventDefault();
                setSubmitText('Submitting...');
                const formData = new FormData(event.currentTarget);

                if (
                  !formData.get('firstName') ||
                  !formData.get('lastName') ||
                  !formData.get('company') ||
                  !formData.get('email') ||
                  !formData.get('privacyPolicy')
                ) {
                  setErrorMessage('All fields are required.');
                  return;
                }

                // Save Answers
                // Cast values from FormData to string (since FormData.get() could return FormDataEntryValue or null)
                setFirstName(formData.get('firstName') as string);
                setLastName(formData.get('lastName') as string);
                setEmail(formData.get('email') as string);
                setCompany(formData.get('company') as string);
                setPrivacyPolicy(formData.get('privacyPolicy') === 'on');

                // Increment Question
                setQuestionIndexStore(0);
                setPillarIndexStore(1);

                // Hide User details modal
                setDisplayModal(!displayModal);

                // Set submit text
                setSubmitText(defaultSubmitText);
              }}
            >
              <Input
                type="text"
                placeholder="First Name"
                label="First Name"
                required
              />
              <Input
                type="text"
                placeholder="Last Name"
                label="Last Name"
                required
              />
              <Input
                type="text"
                placeholder="Company"
                label="Company"
                required
              />
              <Input
                type="email"
                placeholder="you@company.com"
                label="Email"
                required
              />
              <fieldset className="w-full md:w-1/2 p-4 pt-8 flex flex-row items-center gap-2 text-xs text-grey-mid">
                <input
                  id="privacyPolicy"
                  name="privacyPolicy"
                  type="checkbox"
                  required
                  aria-required="true"
                />
                <label htmlFor="privacyPolicy">
                  You agree to our friendly{' '}
                  <a
                    href="https://www.tmforum.org/about-tm-forum/legal-branding/website-privacy-policy/"
                    className="underline hover:no-underline"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>
                </label>
              </fieldset>
              <fieldset className="w-full p-4 flex">
                <Button title={submitText} type="submit" className="!mt-0" />
              </fieldset>
            </form>
            {errorMessage && (
              <p className="mt-5 text-red-500">{errorMessage}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalUserDetails;
